// SPDX-License-Identifier: GPL-2.0-or-later
/**
 * <einvoice-field-browser> - the field-catalogue equivalent of
 * einvoice-rule-browser.mjs (that file's header comment explains the "why virtualise" reasoning in
 * full, not repeated here). Flat catalogue - fields have no "standard" grouping the way rules do, so
 * there is no per-ruleset picker here, just the index + virtual list + lazy per-field detail.
 *
 * Usage:
 *
 *   <einvoice-field-browser
 *     data-src="/wp-content/plugins/einvoice-viewer-technical-details/data/field-catalog"
 *     data-src-protected="/wp-content/plugins/einvoice-viewer-extended-profiles/data/protected/field-catalog"
 *     data-height="480"
 *   ></einvoice-field-browser>
 *
 * `data-src-protected` is optional; without it the browser lists only what `data-src` carries.
 */

import { VirtualList } from './VirtualList.mjs';
import { FieldCatalogClient } from './FieldCatalogClient.mjs';
import { BROWSER_STYLE, escapeHtml, kitStyleLinks } from './browser-style.mjs';
import { setLanguage, t } from '../model/Labels.mjs';

/**
 * The labels of this component.
 *
 * ⚠️ **NO LANGUAGE TABLE OF ITS OWN.** A table here would be a third translation path next to gettext and the
 * shared table, and the worst of the three, because nobody sees it from outside: the texts would stand in no
 * `.pot`, so translate.wordpress.org would not know them; they would stand in no `.po`, so the checking tool
 * would not report them as missing; and a fourth language would need a code change.
 *
 * ⚠️ **THE SHAPE STAYS AN OBJECT WITH SHORT KEYS ON PURPOSE.** The eighteen
 * places of use further down write `this._t.search`. The texts come from `t()`, that is from
 * the `.po` of the plugin through the loaded language table.
 *
 * ⚠️ **THE FUNCTION IS CALLED ONLY AFTER `setLanguage()`, NEVER BEFORE.** `t()` reads the
 * configured language when called; an object created when the module loads would carry
 * English forever.
 */

function labels() {

	/*
	 * ⚠️ **THE SCOPE OPTIONS NAME BOTH STANDARDS.** Options like "Core and Extended", "EN16931 core" and
	 * "Factur-X Extended" leave the operator asking "core and extended - of what?" ( observed 08.10.2026 ). The
	 * select carries the label "Fields", and each option says which list of fields it means. The
	 * select itself only appears with the "Extended Profiles" add-on ( see _boot() ).
	 *
	 * ⚠️ **ONE OPTION PER EXTENSION.** The add-on also carries the fields of the
	 * XRechnung Extension and of EXTENDED-CTC-FR. A common option "Extensions" would mix a German
	 * and a French extension with Factur-X EXTENDED; the first option says "EN 16931 and extensions".
	 * The names of the extensions are proper names and stay untranslated inside the translated sentence.
	 *
	 * ⚠️ **EVERY CONTROL HAS A VISIBLE LABEL.** A placeholder alone disappears with the first keystroke
	 * and is not read by every screen reader; the kit's `.field` puts a small label above each control.
	 */
	return {
		searchLabel:     t( 'Search' ),
		search:          t( 'Search BT/BG code or name …' ),
		kindLabel:       t( 'Field type' ),
		allKinds:        t( 'BT & BG' ),
		bt:              t( 'BT only' ),
		bg:              t( 'BG only' ),
		scopeLabel:      t( 'Fields' ),
		allScopes:       t( 'All fields ( EN 16931 and extensions )' ),
		core:            t( 'EN 16931 only' ),
		extended:        t( 'Factur-X EXTENDED only' ),
		xrExtension:     t( 'XRechnung Extension only' ),
		frCtc:           t( 'EXTENDED-CTC-FR only' ),
		loading:         t( 'Loading …' ),
		title:           t( 'Fields' ),
		colCode:         t( 'Code' ),
		colCardinality:  t( 'Cardinality' ),
		colName:         t( 'Name' ),
		empty:           t( 'No entry matches the filter.' ),
		cardinality:     t( 'Cardinality (EN16931)' ),
		datatype:        t( 'Semantic data type' ),
		reftable:        t( 'Reference table' ),
		ciiPath:         t( 'CII path' ),
		ublPath:         t( 'UBL path' ),
		applicableRules: t( 'Applicable rules' ),
		close:           t( 'Close' ),
		onlyEnglish:     t( 'This text is only available in English.' ),
	};

}

/**
 * The language of the page, as far as this bundle can offer it.
 *
 * ⚠️ **THE LIST IS NOT FIXED HERE.** `setLanguage()` decides by the available languages that
 * `i18n/index.json.js` names. Adding a language thus means adding a file.
 */

function documentLanguage() {
	return setLanguage( ( document.documentElement.lang || 'en' ).slice( 0, 2 ).toLowerCase() );
}

/**
 * The scope of one index row, as the value of the scope filter.
 *
 * ⚠️ THE SCOPE COMES FROM WHERE THE ROW WAS LOADED, NOT FROM THE ID. Every row of
 * the add-on's catalogue ( `source: 'protected'` ) belongs to an extension. The rule "`-X-` in the id
 * means EXTENDED" stays only as the fallback for a host whose own catalogue still lists the extension; it
 * would call 17 fields of the extension with a plain number "EN16931 core" ( measured 08.10.2026 ).
 *
 * ⚠️ `extension` NAMES THE EXTENSION, AND ITS ABSENCE MEANS FACTUR-X EXTENDED. The
 * add-on's build writes `extension` only on the rows of the XRechnung Extension and of EXTENDED-CTC-FR
 * ( `erweiterungsFelder()` in apps/build/build-addons.mjs ). The 963 rows of Factur-X EXTENDED carry no
 * such key, also in an older add-on, so they must not need it.
 *
 * @param {{id: string, source?: string, extension?: string}} r
 * @return {'EN16931'|'EXTENDED'|'XRECHNUNG-EXTENSION'|'EXTENDED-CTC-FR'}
 */

function scopeOf( r ) {

	if ( r.extension === 'xrechnung-extension' ) return 'XRECHNUNG-EXTENSION';
	if ( r.extension === 'fr-ctc' ) return 'EXTENDED-CTC-FR';

	return ( r.source === 'protected' || String( r.id ).includes( '-X-' ) ) ? 'EXTENDED' : 'EN16931';

}

const ROW_HEIGHT = 32;

/*
 * ⚠️ ONLY THE COLUMNS ARE OWN STYLE; EVERYTHING ELSE IS browser-style.mjs. The header row and every list
 * row share one grid template ( `--cols` ), so the column headings stand exactly above their cells even
 * though the rows are absolutely positioned by VirtualList.
 */
const STYLE = BROWSER_STYLE + `
.br-panel { --cols: 120px 96px minmax( 0, 1fr ); }
`;
// ⚠️ Pixels, not `em`: header and rows have different font sizes - see einvoice-rule-browser.mjs.

class EInvoiceFieldBrowser extends HTMLElement {

	connectedCallback() {

		if ( this.dataset.svFbReady === '1' )
			return;

		this.dataset.svFbReady = '1';

		// ⚠️ The language is remembered by the component itself, not only the labels: the field names and
		// descriptions come from the catalogue data and carry their own language fields.
		// ⚠️ Set first, then label - `labels()` reads the configured language.
		this._lang = documentLanguage();
		this._t = labels();
		// ⚠️ `data-src-protected` is the add-on's field-catalog folder, set by the host only when the
		// "Extended Profiles" add-on is active ( see FieldCatalogClient.mjs ). Without it: EN 16931 only.
		this._client = new FieldCatalogClient( this.dataset.src || '', this.dataset.srcProtected || '' );
		this._allRows = [];
		this._filtered = [];

		const shade = this.attachShadow( { mode: 'open' } );

		// ⚠️ The kit from data-css first, then the own rules - see kitStyleLinks().
		kitStyleLinks( shade, this, STYLE );

		const root = document.createElement( 'div' );
		const tx = this._t;

		root.className = 'wrap';

		root.innerHTML = `
			<div class="panel br-panel">
				<div class="panel-head"><h2>${ escapeHtml( tx.title ) }</h2><span class="count stats"></span></div>
				<div class="filterbar">
					<div class="field br-grow"><label for="br-search">${ escapeHtml( tx.searchLabel ) }</label><input id="br-search" type="text" class="inp search" placeholder="${ escapeHtml( tx.search ) }"></div>
					<div class="field"><label for="br-f-kind">${ escapeHtml( tx.kindLabel ) }</label><select id="br-f-kind" class="sel f-kind">
						<option value="">${ escapeHtml( tx.allKinds ) }</option>
						<option value="BT">${ escapeHtml( tx.bt ) }</option>
						<option value="BG">${ escapeHtml( tx.bg ) }</option>
					</select></div>
					<div class="field f-scope-field" hidden><label for="br-f-scope">${ escapeHtml( tx.scopeLabel ) }</label><select id="br-f-scope" class="sel f-scope">
						<option value="">${ escapeHtml( tx.allScopes ) }</option>
						<option value="EN16931">${ escapeHtml( tx.core ) }</option>
						<option value="EXTENDED">${ escapeHtml( tx.extended ) }</option>
						<option value="XRECHNUNG-EXTENSION">${ escapeHtml( tx.xrExtension ) }</option>
						<option value="EXTENDED-CTC-FR">${ escapeHtml( tx.frCtc ) }</option>
					</select></div>
				</div>
				<div class="br-cols" aria-hidden="true"><span>${ escapeHtml( tx.colCode ) }</span><span>${ escapeHtml( tx.colCardinality ) }</span><span>${ escapeHtml( tx.colName ) }</span></div>
				<div class="br-list" style="height:${ parseInt( this.dataset.height || '480', 10 ) }px"></div>
				<div class="empty" hidden>${ escapeHtml( tx.empty ) }</div>
			</div>
			<div class="panel br-detail-panel" hidden></div>
		`;
		shade.appendChild( root );

		this._els = {
			search: root.querySelector( '.search' ),
			kind: root.querySelector( '.f-kind' ),
			scope: root.querySelector( '.f-scope' ),
			scopeField: root.querySelector( '.f-scope-field' ),
			stats: root.querySelector( '.stats' ),
			list: root.querySelector( '.br-list' ),
			empty: root.querySelector( '.empty' ),
			detail: root.querySelector( '.br-detail-panel' ),
		};

		this._virtualList = new VirtualList( {
			container: this._els.list,
			rowHeight: ROW_HEIGHT,
			renderRow: ( item, index ) => this._renderRow( item, index ),
		} );

		this._wire();
		this._boot();

	}

	/** @return {number} DOM row count right now - for tests only. */
	get domRowCount() {
		return this._virtualList ? this._virtualList.domRowCount : 0;
	}

	async _boot() {

		this._els.stats.textContent = this._t.loading;

		const rows = await this._client.index();

		// ⚠️ The reasoning for where a row's scope comes from stands at scopeOf() above the class.
		this._allRows = rows.map( ( r ) => ( { ...r, scope: scopeOf( r ) } ) );

		/*
		 * ⚠️ WITHOUT ANY ROW OF AN EXTENSION THE SCOPE FILTER IS HIDDEN AND RESET. Its options would
		 * yield an empty list, and a popup URL with `svtd_scope=EXTENDED` would open an empty browser
		 * without saying why. Reset after _applyUrlParams(), which may have set it.
		 *
		 * ⚠️ THE SAME HOLDS PER OPTION. An older add-on carries only Factur-X EXTENDED; its browser must
		 * not offer "XRechnung Extension only" as an option that always ends in "No entry matches the filter".
		 */
		const scopes = new Set( this._allRows.map( ( r ) => r.scope ) );
		const hasExtension = [ ...scopes ].some( ( s ) => s !== 'EN16931' );

		// ⚠️ The whole field is hidden, caption and select, not only the select - a caption "Fields" without
		// its control would stand alone in the filter bar.
		this._els.scopeField.hidden = ! hasExtension;

		// ⚠️ `hidden` on an <option> is not enough in every browser ( Safari still offers it ); the
		// option is removed instead. "All fields" ( value '' ) and "EN 16931 only" always stay.
		for ( const option of [ ...this._els.scope.options ] )
			if ( option.value !== '' && option.value !== 'EN16931' && ! scopes.has( option.value ) )
				option.remove();

		this._applyUrlParams();

		// ⚠️ A value that names no remaining option leaves `select.value` at '' by itself; the
		// explicit reset covers the case without any extension, where every option is still there.
		if ( ! hasExtension || ! [ ...this._els.scope.options ].some( ( o ) => o.value === this._els.scope.value ) )
			this._els.scope.value = '';

		this._applyFilters();

	}

	_applyUrlParams() {

		let params;

		try {
			params = new URLSearchParams( window.location.search );
		} catch ( e ) {
			return;
		}

		if ( params.has( 'svtd_search' ) ) this._els.search.value = params.get( 'svtd_search' );
		if ( params.has( 'svtd_kind' ) ) this._els.kind.value = params.get( 'svtd_kind' );
		if ( params.has( 'svtd_scope' ) ) this._els.scope.value = params.get( 'svtd_scope' );

	}

	_applyFilters() {

		const q = this._els.search.value.trim().toLowerCase();
		const kind = this._els.kind.value;
		const scope = this._els.scope.value;

		this._filtered = this._allRows.filter( ( r ) => {

			if ( kind && r.kind !== kind ) return false;
			if ( scope && r.scope !== scope ) return false;

			if ( q ) {
				const name = Object.values( r.businessTerm || {} ).join( ' ' );
				const haystack = `${ r.id } ${ name }`.toLowerCase();
				if ( ! haystack.includes( q ) ) return false;
			}

			return true;

		} );

		// ⚠️ The count is a bare number next to the heading "Fields", so no plural form has to be
		// translated, and no lowercase msgid "fields" is needed.
		this._els.stats.textContent = String( this._filtered.length );
		this._els.empty.hidden = this._filtered.length > 0;
		this._virtualList.setItems( this._filtered );

	}

	_renderRow( item, index ) {

		const el = document.createElement( 'div' );
		el.className = ( index % 2 ) ? 'br-row br-alt' : 'br-row';

		const name = item.businessTerm?.[ this._lang ] ?? item.businessTerm?.en ?? '';

		el.innerHTML = `
			<span class="mono br-code">${ escapeHtml( item.id ) }</span>
			<span class="mono muted">${ escapeHtml( item.cardinality || '' ) }</span>
			<span title="${ escapeHtml( name ) }">${ escapeHtml( name ) }</span>
		`;

		el.addEventListener( 'click', () => this._openDetail( item ) );

		return el;

	}

	async _openDetail( item ) {

		const t = this._t;
		this._els.detail.hidden = false;
		this._els.detail.innerHTML = `<div class="br-loading">${ t.loading }</div>`;

		const detail = await this._client.field( item.id );

		const name = detail.businessTerm?.[ this._lang ] ?? detail.businessTerm?.en ?? item.id;
		const eigene = detail.description?.[ this._lang ] ?? '';
		const desc   = eigene !== '' ? eigene : ( detail.description?.en ?? '' );

		/*
		 * ⚠️ An English text on a German page is marked as such. The fallback to English is right here;
		 * without the notice the English sentence reads like a translation that does not exist. The PHP
		 * side handles this the same way, see `notice_only_english` in `build-site.php`.
		 */

		const hinweis = ( this._lang !== 'en' && eigene === '' && desc !== '' )
			? `<p class="note warn">${ escapeHtml( t.onlyEnglish ) }</p>`
			: '';

		let rulesHtml = '';
		for ( const [ standard, ids ] of Object.entries( detail.rules || {} ) )
			if ( ids.length )
				rulesHtml += `<dt>${ escapeHtml( standard ) }</dt><dd class="mono">${ escapeHtml( ids.join( ', ' ) ) }</dd>`;

		this._els.detail.innerHTML = `
			<div class="panel-head"><h2><span class="mono br-code">${ escapeHtml( item.id ) }</span> — ${ escapeHtml( name ) }</h2><div class="panel-actions"><button type="button" class="icon-btn close" aria-label="${ escapeHtml( t.close ) }" title="${ escapeHtml( t.close ) }">&times;</button></div></div>
			<div class="panel-body pad br-detail">
				<p>${ escapeHtml( desc ) }</p>
				${ hinweis }
				<dl>
					<dt>${ escapeHtml( t.cardinality ) }</dt><dd class="mono">${ escapeHtml( detail.en16931SemanticCardinality || '' ) }</dd>
					${ detail.semanticDataType ? `<dt>${ escapeHtml( t.datatype ) }</dt><dd>${ escapeHtml( detail.semanticDataType ) }</dd>` : '' }
					${ detail.referenceTable ? `<dt>${ escapeHtml( t.reftable ) }</dt><dd>${ escapeHtml( detail.referenceTable ) }</dd>` : '' }
					${ detail.ublPath ? `<dt>${ escapeHtml( t.ublPath ) }</dt><dd class="mono">${ escapeHtml( detail.ublPath ) }</dd>` : '' }
					${ detail.xpathUnCefact22B?.xpath ? `<dt>${ escapeHtml( t.ciiPath ) }</dt><dd class="mono">${ escapeHtml( detail.xpathUnCefact22B.xpath ) }</dd>` : '' }
				</dl>
				${ rulesHtml ? `<h4>${ escapeHtml( t.applicableRules ) }</h4><dl>${ rulesHtml }</dl>` : '' }
			</div>
		`;

		this._els.detail.querySelector( '.close' ).addEventListener( 'click', () => {
			this._els.detail.hidden = true;
		} );

	}

	_wire() {

		let searchTimer = null;
		const onFilterChange = () => {
			window.clearTimeout( searchTimer );
			searchTimer = window.setTimeout( () => this._applyFilters(), 100 );
		};

		this._els.search.addEventListener( 'input', onFilterChange );
		this._els.kind.addEventListener( 'change', () => this._applyFilters() );
		this._els.scope.addEventListener( 'change', () => this._applyFilters() );

	}

}

if ( typeof customElements !== 'undefined' && ! customElements.get( 'einvoice-field-browser' ) )
	customElements.define( 'einvoice-field-browser', EInvoiceFieldBrowser );
